import h from 'hyperscript';
import type { HyperScriptProps } from 'hyperscript';

import { addStyleClass } from '@disy/lib/add-style-class';
import type { ButtonSize, ToggleButton } from '@disy/ui/button';
import type { Icon } from '@disy/ui/icon';

import './icon-toggle.css';

export interface IconToggleOptions extends HyperScriptProps {
  onclick?: (e: Event) => void | Promise<void>;
  pressed?: boolean;
  size?: ButtonSize;
  styleClass?: string | string[];
  disabled?: boolean;
}

/**
 * Creates a toggle button with two icons, which are toggled with the pressed state.
 *
 * _Note:_ There's only one title, because toggle buttons should never
 * change label and state at the same time.
 *
 * @param title - The button title
 * @param icon - The default icon
 * @param pressedIcon - The icon for the pressed state
 * @param [options] - Options
 * @param options.props - html properties to be set when creating the icon toggle
 * @param options.onclick - icon toggle callback or promise.  Note that only using a
 * promise currently allows to condition the toggle with the outcome of an operation.
 * @param options.pressed - initial pressed state
 * @param options.size - size of the toggle
 * @param options.styleClass - Additional styleClass
 * @param options.disabled - Disabled state of the toggle.
 * @return The icon toggle button
 * @see https://inclusive-components.design/toggle-button/
 */
export function createIconToggle(
  title: string,
  icon: Icon,
  pressedIcon: Icon,
  { onclick, pressed = false, size, styleClass, disabled = false, ...props }: IconToggleOptions = {},
): ToggleButton {
  const toggle = h<HTMLButtonElement>(
    'button.d-icon-toggle.focus-outline-keynav',
    {
      ...props,
      type: 'button',
      title,
      disabled,
      onclick(event: Event) {
        const previousPressedValue = this.pressed;

        // Instant click before calling other click handlers seems to be required for the
        // callback case.
        this.pressed = !this.pressed;

        if (onclick) {
          // This will already handle the callback case if onclick is a simple callback function.
          const onClickResult = onclick.call(this, event) as object;

          // If it is a promise, revert the change in case of errors
          if (onClickResult instanceof Promise) {
            this.dCurrentOnClickPromise = onClickResult;
            onClickResult
              .catch((error) => {
                if (this.dCurrentOnClickPromise === onClickResult) {
                  this.pressed = previousPressedValue;
                }
                throw error;
              })
              .finally(() => {
                delete this.dCurrentOnClickPromise;
              });
          }

          return onClickResult;
        }
      },
    },
    [icon, pressedIcon],
  );

  if (size) {
    toggle.classList.add(`button-${size}`);
  }
  addStyleClass(toggle, styleClass);

  return asToggleButton(toggle, !!pressed);
}

function asToggleButton(button: HTMLButtonElement, pressed: boolean): ToggleButton {
  Object.defineProperty(button, 'pressed', {
    get() {
      return this.getAttribute('aria-pressed') === 'true';
    },
    set(value) {
      this.setAttribute('aria-pressed', Boolean(value));
    },
  });
  const toggleButton = button as ToggleButton;
  toggleButton.pressed = pressed;
  return toggleButton;
}
